/**
 * Brutal mode voices (LEV-2442): the toughest reviewers each program's
 * application faces, as display data: names, roles, LinkedIn URLs and
 * sources. One persona feeds three surfaces, so they read as one voice: the
 * Brutal mode banner (BrutalBanner), the per-answer Brutal evaluator's prompt
 * and the whole-application Brutal read (BrutalVerdictCard and
 * evaluateBrutalVerdict).
 *
 * LEV-2526 split this module's prompt-only text (what Lev predicts each
 * person pushes on, their likely questions, the attribution examples and the
 * built prompt intro) into src/lib/ai/accelerators/brutalPersonaPrompts.ts, a
 * "server-only" module: that text is never shown to a founder, and this file
 * is imported by three "use client" components (BrutalBanner,
 * EvaluationPanel, BrutalVerdictCard), so anything added here ships straight
 * to the browser bundle whether or not the UI reads it. A field belongs here
 * only if a client component may render it.
 *
 * A named person appears only with a verified role and sources from the
 * program's official site or the person's own. Every line about a person is a
 * likely reading going by their public record, never a verdict stated as fact
 * and never a quote (LEV-2745, T.A.: "be suggestive on how they might judge"),
 * and nothing may imply they read the application, work with Lev or endorse
 * it. Founder copy carries no em or en dash
 * (docs/contributing/product-copy-voice.md). The brutalPersona test enforces
 * all of it.
 *
 * `name: null` is a person the operator has not yet approved naming: they
 * render by `role`, and adding the name later is a data change only.
 *
 * Client-safe (no "use client", no server-only import, and this module never
 * imports programApplications.ts, which imports the personas).
 */
import { PROGRAMS } from "@/constants/programs";

export interface BrutalSource {
  url: string;
  title: string;
}

export interface BrutalPerson {
  /** Null until naming is approved; the person then renders by `role`. */
  name: string | null;
  /** How founder copy names an unnamed person, lowercase-led ("the managing director of Techstars NYC"). */
  role: string;
  /** "Scribd co-founder", from an official page; null shows none. */
  credential: string | null;
  /** Official people page or the person's own site, where the role was verified. */
  profileUrl: string;
  /**
   * The read card links a named person to this LinkedIn profile, fetched
   * from the program's own people page (never guessed: a wrong guessed
   * LinkedIn URL has burned Lev before). Null leaves the name plain text,
   * for a person whose page lists none or who is still `name: null`.
   */
  linkedinUrl: string | null;
  /** What their public record suggests they push on, as the banner's object phrase ("your traction numbers"). */
  probes: string;
  /** ISO date the role and sources were checked. */
  verifiedOn: string;
  sources: readonly BrutalSource[];
}

export interface BrutalLead extends BrutalPerson {
  /** Whose record the banner reads them by ("his public writing"). */
  publicWriting: string;
  /** Lev's words on what that record suggests they look for; never a quote, never a verdict. */
  stance: string | null;
}

export interface BrutalPersona {
  programName: string;
  /** Null is the generic toughest reviewer: no names, no links, no people page. */
  lead: BrutalLead | null;
  /** 2 or 3 people with a lead; empty without one. */
  probers: readonly BrutalPerson[];
  /** The generic reviewer, as founder copy names them ("the toughest Antler partner"). */
  reviewer: string;
  /** Who a persona with no lead reads like, by role ("a tough Antler partner"). */
  toughReader: string;
  /** The banner's line on what the reader pushes on when there are no probers. */
  probesSummary: string;
  /** Banner and read card: "Most apps land between 3 and 5. A 7 means ...". */
  calibration: string;
  peoplePage: { label: string; url: string } | null;
  /**
   * Who the Brutal read card's explainer line names (LEV-2642), when the
   * program already has a person named elsewhere for scoring/Coach while
   * `lead.name` here stays null pending that program's own OK on the
   * banner/rubric (e.g. Techstars' David Cohen, ProgramReviewerPersona.who
   * in programApplications.ts). A literal duplicate of that string, not an
   * import: this module must never import
   * programApplications.ts (see file header). Explainer-only: never read by
   * the banner, the rubric, or anything the model sees. Null when `lead.name`
   * already names someone, or when no one is named anywhere.
   */
  explainerReaderName: string | null;
}

export const BRUTAL_CLOSER = "It's harsh, but better to hear it from Lev than from a rejection email.";

/** How copy names a person: their name, else their role. */
export function brutalPersonLabel(person: Pick<BrutalPerson, "name" | "role">): string {
  return person.name ?? person.role;
}

/** A role at a sentence start ("The managing director ..."). */
export function capitalizeBrutalLabel(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Every person the persona names or describes, lead first. */
export function brutalPeople(persona: Pick<BrutalPersona, "lead" | "probers">): BrutalPerson[] {
  return persona.lead ? [persona.lead, ...persona.probers] : [];
}

/**
 * Who the banner and the Brutal wait line say the scores read like: the lead,
 * as a likely reading of their public writing, else the generic tough
 * reviewer. `verb` completes "the way X ..." ("might" for a person).
 */
export function brutalBannerReader(persona: Pick<BrutalPersona, "lead" | "toughReader">): {
  label: string;
  person: BrutalLead | null;
  verb: string;
  basis: string | null;
} {
  const { lead } = persona;
  return lead
    ? { label: brutalPersonLabel(lead), person: lead, verb: "might", basis: `going by ${lead.publicWriting}` }
    : { label: persona.toughReader, person: null, verb: "would", basis: null };
}

/** Who the read card's explainer names (LEV-2642): the lead's own name, else `explainerReaderName`, else no one. */
export function brutalExplainerReaderName(
  persona: Pick<BrutalPersona, "lead" | "explainerReaderName">,
): string | null {
  return persona.lead?.name ?? persona.explainerReaderName;
}

const YC_VERIFIED_ON = "2026-10-19";

export const YC_BRUTAL_PERSONA: BrutalPersona = {
  programName: PROGRAMS.yc.fullName,
  lead: {
    name: "Garry Tan",
    role: "YC's president and CEO",
    credential: "Posterous and Initialized Capital co-founder",
    profileUrl: "https://www.ycombinator.com/people/garry-tan",
    linkedinUrl: "https://www.linkedin.com/in/garrytan",
    probes: "whether you sound like an earnest founder talking",
    verifiedOn: YC_VERIFIED_ON,
    sources: [
      { url: "https://www.ycombinator.com/people/garry-tan", title: "Garry Tan, Y Combinator" },
      { url: "https://www.ycombinator.com/people", title: "People, Y Combinator" },
    ],
    publicWriting: "his public writing",
    // Lev's words: Garry's "earnestness" and "new discovery" lines were not
    // found verbatim on any official page, so they are not quoted.
    stance:
      "Expect him to look for an earnest founder talking, not marketing copy, and for something you discovered by building with the technology itself.",
  },
  probers: [
    {
      name: "Jared Friedman",
      role: "a YC managing director",
      credential: "Scribd co-founder",
      profileUrl: "https://www.ycombinator.com/people/jared-friedman",
      linkedinUrl: "https://www.linkedin.com/in/jaredfriedman",
      probes: "your traction numbers",
      verifiedOn: YC_VERIFIED_ON,
      sources: [{ url: "https://www.ycombinator.com/people/jared-friedman", title: "Jared Friedman, Y Combinator" }],
    },
    {
      name: "Tom Blomfield",
      role: "a YC general partner",
      credential: "Monzo co-founder",
      profileUrl: "https://www.ycombinator.com/people/tom-blomfield",
      linkedinUrl: "https://www.linkedin.com/in/tomblomfield",
      probes: "why you and not an incumbent",
      verifiedOn: YC_VERIFIED_ON,
      sources: [{ url: "https://www.ycombinator.com/people/tom-blomfield", title: "Tom Blomfield, Y Combinator" }],
    },
    {
      name: "Nicolas Dessaigne",
      role: "a YC general partner",
      credential: "Algolia co-founder",
      profileUrl: "https://www.ycombinator.com/people/nicolas-dessaigne",
      linkedinUrl: "https://www.linkedin.com/in/nicolasdessaigne",
      probes: "your technical moat",
      verifiedOn: YC_VERIFIED_ON,
      sources: [{ url: "https://www.ycombinator.com/people/nicolas-dessaigne", title: "Nicolas Dessaigne, Y Combinator" }],
    },
  ],
  reviewer: "the toughest YC partner",
  toughReader: "a tough YC partner",
  probesSummary: "",
  calibration: "Most apps land between 3 and 5. A 7 means a partner would fight to interview you.",
  peoplePage: { label: "Who reads YC applications", url: "https://www.ycombinator.com/people" },
  // lead.name already names Garry Tan; no explainer fallback needed.
  explainerReaderName: null,
};

const TECHSTARS_VERIFIED_ON = "2026-10-19";

/**
 * Names held back (T.A., Oct 19: "Roles now, names after OK"): every person
 * has `name: null` and links to a program page, not their own, until naming
 * is approved. (The lens paragraphs that judge in Techstars' own selection
 * order live with the prompt fields, in brutalPersonaPrompts.ts.)
 */
export const TECHSTARS_BRUTAL_PERSONA: BrutalPersona = {
  programName: PROGRAMS.techstars.fullName,
  lead: {
    name: null,
    role: "Techstars' founder and CEO",
    credential: null,
    profileUrl: "https://www.techstars.com/team",
    linkedinUrl: null,
    probes: "the team first",
    verifiedOn: TECHSTARS_VERIFIED_ON,
    sources: [
      { url: "https://www.techstars.com/team/david-cohen", title: "Techstars team" },
      { url: "https://www.techstars.com/team", title: "Leadership, Techstars team" },
    ],
    publicWriting: "their public writing",
    stance: "Expect the team to be read first, and everything else after.",
  },
  probers: [
    {
      name: null,
      role: "the managing director of Techstars NYC",
      credential: null,
      profileUrl: "https://www.techstars.com/accelerators/nyc",
      linkedinUrl: null,
      probes: "whether you can sell this yourselves",
      verifiedOn: TECHSTARS_VERIFIED_ON,
      sources: [
        { url: "https://www.techstars.com/team/andres-barreto", title: "Techstars team" },
        { url: "https://www.techstars.com/accelerators/nyc", title: "Techstars NYC Accelerator" },
      ],
    },
    {
      name: null,
      role: "the managing director of Techstars Chicago",
      credential: null,
      profileUrl: "https://www.techstars.com/accelerators/chicago",
      linkedinUrl: null,
      probes: "what keeps you shipping once the program ends",
      verifiedOn: TECHSTARS_VERIFIED_ON,
      sources: [{ url: "https://www.techstars.com/accelerators/chicago", title: "Techstars Chicago Accelerator" }],
    },
    {
      name: null,
      role: "the managing director of Techstars London",
      credential: null,
      profileUrl: "https://www.techstars.com/accelerators/london",
      linkedinUrl: null,
      probes: "whether you could raise your next round on this pitch",
      verifiedOn: TECHSTARS_VERIFIED_ON,
      sources: [
        { url: "https://www.techstars.com/team/georgie-smithwick", title: "Techstars team" },
        { url: "https://www.techstars.com/accelerators/london", title: "Techstars London Accelerator" },
      ],
    },
  ],
  reviewer: "the toughest Techstars managing director",
  toughReader: "a tough Techstars managing director",
  probesSummary: "",
  calibration: "Most apps land between 3 and 5. A 7 means a managing director would fight to bring you into the program.",
  peoplePage: { label: "See the Techstars team →", url: "https://www.techstars.com/team" },
  // The read card's explainer names David Cohen (matching
  // TECHSTARS_REVIEWER_PERSONA.who in programApplications.ts); lead.name
  // stays null above until Techstars OKs naming for the banner and rubric
  // too.
  explainerReaderName: "David Cohen",
};

/** The fallback for a program with no persona of its own: one toughest reviewer, named only by the spec's own reviewerNoun. */
export function genericBrutalPersona({
  programName,
  reviewerNoun,
}: {
  programName: string;
  reviewerNoun: string;
}): BrutalPersona {
  return {
    programName,
    lead: null,
    probers: [],
    reviewer: `the toughest ${reviewerNoun}`,
    toughReader: `a tough ${reviewerNoun}`,
    explainerReaderName: null,
    probesSummary:
      "Expect pushback on whether you sound earnest, whether your traction is real, why you win over the incumbent, and what is hard to copy.",
    calibration: "Most apps land between 3 and 5. A 7 means a reviewer would fight to interview you.",
    peoplePage: null,
  };
}

/**
 * The spec fields a persona is chosen from. Structural, so this module never
 * imports programApplications.ts, which imports the personas; every
 * ProgramApplicationFlowSpec satisfies it.
 */
export type BrutalPersonaSource =
  | { tier: "premier"; brutalPersona: BrutalPersona }
  | { tier: "landing"; programName: string; reviewerNoun: string };

/**
 * The one adapter from a spec to its persona: a premier program's own, else
 * the generic reviewer. A rename of the spec's persona field lands here only.
 */
export function getBrutalPersona(spec: BrutalPersonaSource): BrutalPersona {
  return spec.tier === "premier" ? spec.brutalPersona : genericBrutalPersona(spec);
}
